import React from "react";
import ApiClient from "../../api/ApiClient";
import EndPoint from "../../api/Endpoints";

/* ─────────────────────────────────────────────────────────────
   UserProfileDrawer — panel lateral de perfil de usuario.
   Tabs: Información / Actividad / Cambiar contraseña
───────────────────────────────────────────────────────────── */
const UserProfileDrawer = ({ open, onClose, userid, defConfig, defLang }) => {
  const [userInfo, setUserInfo] = React.useState(null);
  const [loadingInfo, setLoadingInfo] = React.useState(false);
  const [tab, setTab] = React.useState("info"); // "info" | "activity" | "password"

  /* Activity tab state */
  const [activityData, setActivityData] = React.useState(null);
  const [loadingActivity, setLoadingActivity] = React.useState(false);
  const activityLoaded = React.useRef(false);

  /* Password state */
  const [oldPass, setOldPass] = React.useState("");
  const [newPass, setNewPass] = React.useState("");
  const [confirmPass, setConfirmPass] = React.useState("");
  const [showOld, setShowOld] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [passMsg, setPassMsg] = React.useState(null);

  /* Escape key */
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  /* Load user info when drawer opens */
  React.useEffect(() => {
    if (!open || !userid) return;
    setLoadingInfo(true);
    setUserInfo(null);
    setTab("info");
    setPassMsg(null);
    setOldPass("");
    setNewPass("");
    setConfirmPass("");
    activityLoaded.current = false;
    setActivityData(null);
    ApiClient.doPost(EndPoint.getUserSingle, { id: userid })
      .then((res) => {
        if (res && res.status && res.data) setUserInfo(res.data);
      })
      .catch(() => {})
      .finally(() => setLoadingInfo(false));
  }, [open, userid]);

  /* Load activity data on first visit to activity tab */
  React.useEffect(() => {
    if (tab !== "activity" || activityLoaded.current || !userid) return;
    activityLoaded.current = true;
    setLoadingActivity(true);
    Promise.all([
      ApiClient.doPost(EndPoint.getResumeTasksByUser, { userid }).catch(() => null),
      ApiClient.doPost(EndPoint.getActorsByUser, { userid }).catch(() => null),
    ]).then(([tasksRes, actorsRes]) => {
      const tasks = tasksRes?.result || tasksRes?.data || tasksRes?.rows || [];
      const rows = actorsRes?.result || actorsRes?.data || actorsRes?.rows || [];

      const seenActors = new Set();
      const seenOffices = new Set();
      const actors = [];
      const offices = [];
      rows.forEach((r) => {
        if (!r.ACTOR) return;
        const parts = r.ACTOR.split(" - ");
        const officeName = parts[0]?.trim();
        const actorName = parts.slice(1).join(" - ").trim();
        if (officeName && !seenOffices.has(officeName)) {
          seenOffices.add(officeName);
          offices.push({ name: officeName });
        }
        if (actorName && !seenActors.has(actorName)) {
          seenActors.add(actorName);
          actors.push({ name: actorName });
        }
      });

      const totalTasks = tasks.reduce((s, r) => s + (Number(r.TOTAL) || 0), 0);
      const assignedTasks = tasks.reduce((s, r) => s + (Number(r.TOTAL_ASSIGNED) || 0), 0);
      const outTasks = tasks.reduce((s, r) => s + (Number(r.TOTAL_OUT) || 0), 0);

      setActivityData({ totalTasks, assignedTasks, outTasks, actors, offices });
    }).finally(() => setLoadingActivity(false));
  }, [tab, userid]);

  const handleSavePassword = async () => {
    setPassMsg(null);
    if (newPass.length < 8) {
      setPassMsg({ type: "err", text: defConfig?.[defLang]?.messagePasswordVal0 || "La contraseña debe tener al menos 8 caracteres." });
      return;
    }
    if (newPass !== confirmPass) {
      setPassMsg({ type: "err", text: defConfig?.[defLang]?.messagePasswordVal1 || "Las contraseñas no coinciden." });
      return;
    }
    if (newPass === oldPass) {
      setPassMsg({ type: "err", text: defConfig?.[defLang]?.messagePasswordVal2 || "La nueva contraseña debe ser diferente a la actual." });
      return;
    }
    setSaving(true);
    try {
      const res = await ApiClient.doPut(EndPoint.updatePassword, {
        password: newPass,
        old_password: oldPass,
      });
      if (res && res.status) {
        setPassMsg({ type: "ok", text: defConfig?.[defLang]?.passwordchangedsuccess || "Contraseña actualizada correctamente." });
        setOldPass("");
        setNewPass("");
        setConfirmPass("");
      } else {
        setPassMsg({ type: "err", text: res?.message || defConfig?.[defLang]?.passwordchangedfailed || "Error al cambiar la contraseña." });
      }
    } catch {
      setPassMsg({ type: "err", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const isMobile = window.innerWidth < 799;
  const drawerWidth = isMobile ? "100%" : "380px";

  const initials = userInfo
    ? ((userInfo.FIRSTNAME?.[0] || "") + (userInfo.LASTNAME?.[0] || "")).toUpperCase()
    : "?";

  return (
    <div className="xfd-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="xfd-drawer xpd-drawer"
        style={{ width: drawerWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="xfd-header">
          <div className="xfd-header-title">
            <span className="e-icons e-profile xfd-header-icon"></span>
            <h2 className="xfd-header-text">
              {defConfig?.[defLang]?.myprofilelabel || "Mi Perfil"}
            </h2>
          </div>
          <button type="button" className="xfd-close-btn" onClick={onClose} title="Cerrar">
            <span className="e-icons e-close"></span>
          </button>
        </div>

        {/* Tab bar */}
        <div className="xpd-tabs">
          <button
            className={"xpd-tab" + (tab === "info" ? " xpd-tab-active" : "")}
            onClick={() => setTab("info")}
          >
            <span className="e-icons e-contact"></span>
            {defConfig?.[defLang]?.profileinfotab || "Información"}
          </button>
          <button
            className={"xpd-tab" + (tab === "activity" ? " xpd-tab-active" : "")}
            onClick={() => setTab("activity")}
          >
            <span className="e-icons e-chart"></span>
            Actividad
          </button>
          <button
            className={"xpd-tab" + (tab === "password" ? " xpd-tab-active" : "")}
            onClick={() => setTab("password")}
          >
            <span className="e-icons e-lock"></span>
            {defConfig?.[defLang]?.changepassword || "Contraseña"}
          </button>
        </div>

        {/* Body */}
        <div className="xfd-body xpd-body">
          {/* ── INFORMACIÓN ── */}
          {tab === "info" && (
            <div className="xpd-info-panel">
              {loadingInfo ? (
                <div className="xpd-loading">
                  <div className="xpd-spinner"></div>
                  <span>Cargando perfil…</span>
                </div>
              ) : userInfo ? (
                <>
                  <div className="xpd-avatar-wrap">
                    <div className="xpd-avatar">{initials}</div>
                    <div className="xpd-avatar-name">
                      {userInfo.FIRSTNAME} {userInfo.LASTNAME}
                    </div>
                    <div className="xpd-avatar-role">{userInfo.USERGROUPNAME}</div>
                  </div>

                  <div className="xpd-fields">
                    <InfoField label={defConfig?.[defLang]?.usernamelabel || "Usuario"} value={userInfo.USERNAME} icon="e-profile" />
                    <InfoField label={defConfig?.[defLang]?.namelabel || "Nombre"} value={userInfo.FIRSTNAME} icon="e-group-2" />
                    <InfoField label={defConfig?.[defLang]?.lastnamelabel || "Apellido"} value={userInfo.LASTNAME} icon="e-group-2" />
                    <InfoField label={defConfig?.[defLang]?.emaillabel || "Email"} value={userInfo.EMAIL} icon="e-changes-track" />
                    {userInfo.STATUS !== undefined && (
                      <div className="xpd-field-row">
                        <span className="xpd-field-icon e-icons e-check-box"></span>
                        <div className="xpd-field-content">
                          <span className="xpd-field-label">{defConfig?.[defLang]?.statuslabel || "Estado"}</span>
                          <span className={"xpd-status-badge " + (userInfo.STATUS === "ENABLED" ? "xpd-status-active" : "xpd-status-inactive")}>
                            {defConfig?.[defLang]?.[userInfo.STATUS]
                              || (userInfo.STATUS === "ENABLED"
                                  ? defConfig?.[defLang]?.activelabel || "Activo"
                                  : defConfig?.[defLang]?.inactivelabel || "Inactivo")}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="xpd-no-data">No se pudo cargar la información del usuario.</p>
              )}
            </div>
          )}

          {/* ── ACTIVIDAD ── */}
          {tab === "activity" && (
            <div className="xpd-activity-panel">
              {loadingActivity ? (
                <div className="xpd-loading">
                  <div className="xpd-spinner"></div>
                  <span>Cargando actividad…</span>
                </div>
              ) : activityData ? (
                <>
                  {/* Task stats */}
                  <div className="xpd-act-section">
                    <h3 className="xpd-act-section-title">
                      <span className="e-icons e-task"></span>
                      Tareas
                    </h3>
                    <div className="xpd-stat-grid">
                      <div className="xpd-stat-card">
                        <span className="xpd-stat-num">{activityData.totalTasks}</span>
                        <span className="xpd-stat-label">Disponibles</span>
                      </div>
                      <div className="xpd-stat-card xpd-stat-card--accent">
                        <span className="xpd-stat-num">{activityData.assignedTasks}</span>
                        <span className="xpd-stat-label">Mis asignadas</span>
                      </div>
                      <div className="xpd-stat-card xpd-stat-card--warn">
                        <span className="xpd-stat-num">{activityData.outTasks}</span>
                        <span className="xpd-stat-label">Vencidas</span>
                      </div>
                    </div>
                  </div>

                  {/* Actors */}
                  <div className="xpd-act-section">
                    <h3 className="xpd-act-section-title">
                      <span className="e-icons e-people"></span>
                      Actores asignados
                      <span className="xpd-act-count">{activityData.actors.length}</span>
                    </h3>
                    {activityData.actors.length === 0 ? (
                      <p className="xpd-act-empty">Sin actores asignados</p>
                    ) : (
                      <div className="xpd-badge-list">
                        {activityData.actors.map((a, i) => (
                          <span key={i} className="xpd-badge xpd-badge--actor" title={a.name}>
                            {a.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Offices */}
                  <div className="xpd-act-section">
                    <h3 className="xpd-act-section-title">
                      <span className="e-icons e-home"></span>
                      Oficinas asignadas
                      <span className="xpd-act-count">{activityData.offices.length}</span>
                    </h3>
                    {activityData.offices.length === 0 ? (
                      <p className="xpd-act-empty">Sin oficinas asignadas</p>
                    ) : (
                      <div className="xpd-badge-list">
                        {activityData.offices.map((o, i) => (
                          <span key={i} className="xpd-badge xpd-badge--office" title={o.name}>
                            {o.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="xpd-no-data">No se pudo cargar la actividad.</p>
              )}
            </div>
          )}

          {/* ── CONTRASEÑA ── */}
          {tab === "password" && (
            <div className="xpd-pass-panel">
              <p className="xpd-pass-hint">
                {defConfig?.[defLang]?.changepasswordhint || "Ingresa tu contraseña actual y elige una nueva de al menos 8 caracteres."}
              </p>

              {passMsg && (
                <div className={"xpd-msg " + (passMsg.type === "ok" ? "xpd-msg-ok" : "xpd-msg-err")}>
                  <span className={"e-icons " + (passMsg.type === "ok" ? "e-check" : "e-circle-close")}></span>
                  {passMsg.text}
                </div>
              )}

              <PasswordField
                label={defConfig?.[defLang]?.oldpasswordlabel || "Contraseña actual"}
                value={oldPass}
                show={showOld}
                onToggle={() => setShowOld((v) => !v)}
                onChange={(e) => setOldPass(e.target.value)}
              />
              <PasswordField
                label={defConfig?.[defLang]?.newpasswordlabel || "Nueva contraseña"}
                value={newPass}
                show={showNew}
                onToggle={() => setShowNew((v) => !v)}
                onChange={(e) => setNewPass(e.target.value)}
              />
              <PasswordField
                label={defConfig?.[defLang]?.repeatpasswordlabel || "Confirmar contraseña"}
                value={confirmPass}
                show={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
                onChange={(e) => setConfirmPass(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Footer (solo en tab password) */}
        {tab === "password" && (
          <div className="xfd-footer">
            <button type="button" className="xfd-cancel-btn" onClick={onClose}>
              <span className="e-icons e-close"></span>
              {defConfig?.[defLang]?.cancellabel || "Cancelar"}
            </button>
            <button
              type="button"
              className="xfd-action-btn"
              onClick={handleSavePassword}
              disabled={saving}
            >
              {saving ? (
                <span className="xpd-btn-spinner"></span>
              ) : (
                <span className="e-icons e-check"></span>
              )}
              {defConfig?.[defLang]?.changelabel || "Cambiar"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── InfoField sub-component ── */
const InfoField = ({ label, value, icon }) => (
  <div className="xpd-field-row">
    <span className={"xpd-field-icon e-icons " + icon}></span>
    <div className="xpd-field-content">
      <span className="xpd-field-label">{label}</span>
      <span className="xpd-field-value">{value || "—"}</span>
    </div>
  </div>
);

/* ── PasswordField sub-component ── */
const PasswordField = ({ label, value, show, onToggle, onChange }) => (
  <div className="xpd-input-group">
    <label className="xpd-input-label">{label}</label>
    <div className="xpd-input-wrap">
      <input
        type={show ? "text" : "password"}
        className="xpd-input"
        value={value}
        onChange={onChange}
        autoComplete="new-password"
      />
      <button type="button" className="xpd-eye-btn" onClick={onToggle} tabIndex={-1} title={show ? "Ocultar" : "Mostrar"}>
        <span className={"e-icons e-eye"} style={{ opacity: show ? 1 : 0.45 }}></span>
      </button>
    </div>
  </div>
);

export default UserProfileDrawer;
